"use client";

import { useRef, useState } from "react";
import { AlertTriangle, CheckCircle, Copy, Loader2, Mail, Printer, RotateCcw, Send, Upload } from "lucide-react";
import type { Answers, BriefingConfig, Section } from "../types";
import { answersAsPlainText, buildExportPayload } from "../utils/exportBriefing";
import { deriveMeta } from "../utils/meta";
import { countRequiredAnswered, countRequiredMissing, countRequiredTotal } from "../utils/questions";
import { parseBriefingImport } from "../utils/importBriefing";
import { buildMailtoHref, sendBriefing } from "../utils/sendBriefing";

interface BriefingActionsProps {
  /** Only the two parts of the catalog this panel needs — any briefing's data satisfies it. */
  data: { sections: Section[]; actions: Record<string, string> };
  config: BriefingConfig;
  answers: Answers;
  onImport: (answers: Answers) => void;
  onReset: () => void;
  hasHydrated: boolean;
  sentAt: string | null;
  changedSinceSent: boolean;
  onMarkSent: (answers: Answers) => void;
}

const utilityButton =
  "inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium px-3 py-2 hover:border-abis-primary/40 hover:text-abis-primary transition-colors";

export default function BriefingActions({
  data,
  config,
  answers,
  onImport,
  onReset,
  hasHydrated,
  sentAt,
  changedSinceSent,
  onMarkSent,
}: BriefingActionsProps) {
  const meta = deriveMeta(answers, config.metaMap);
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [sendState, setSendState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  // The reason the send failed, kept next to the friendly message. Without it a
  // revoked Slack webhook (403 invalid_token), an undeployed env var (500) and a
  // dropped connection all read as the same "Übermittlung fehlgeschlagen", and
  // nobody — client or DBI — can tell which one to act on.
  const [sendError, setSendError] = useState<string | null>(null);
  const [importWarning, setImportWarning] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const total = countRequiredTotal(data.sections, answers);
  const answered = countRequiredAnswered(data.sections, answers);
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0;

  const actions = data.actions;
  const requiredMissing = countRequiredMissing(data.sections, answers);
  const canSend = requiredMissing === 0;
  const isSent = sentAt !== null;
  const sentLabel = sentAt ? new Date(sentAt).toLocaleString("de-DE") : "";

  function handlePrint() {
    window.print();
  }

  function handleCopy() {
    const text = answersAsPlainText(data.sections, meta, answers, config.plainTextTitle);
    navigator.clipboard.writeText(text).then(() => {
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 2000);
    });
  }

  function copyAndOpenMail() {
    const text = answersAsPlainText(data.sections, meta, answers, config.plainTextTitle);
    navigator.clipboard.writeText(text).catch(() => {
      // Clipboard blocked (permissions) — the mail body still instructs pasting;
      // the user can use "Antworten kopieren" below instead.
    });
    openMailClient();
  }

  function openMailClient() {
    const mailtoHref = buildMailtoHref(
      actions.mailtoSubjectTemplate || "Briefing {company}",
      actions.mailtoBodyTemplate || "",
      meta.company
    );
    // Anchor click rather than location.href — more reliable for external
    // protocol handlers, and it must run before any Blob download, which would
    // otherwise consume the user-activation Chrome requires for mailto:.
    const link = document.createElement("a");
    link.href = mailtoHref;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Transmits to DBI (Slack) — the only path that sends data anywhere, hence
  // the consent gate.
  async function handleSendEmail() {
    if (!canSend || !consentAccepted || sendState === "sending") return;
    setSendState("sending");
    setSendError(null);
    const payload = buildExportPayload(data.sections, meta, answers, config.id);
    const result = await sendBriefing(payload, config.sendEndpoint);
    if (!result.ok) {
      // Also on the console: the visible detail line is deliberately terse, and
      // this is what a client can screenshot when they report the failure.
      console.error(`[${config.id}] Übermittlung fehlgeschlagen:`, result.status, result.error);
      setSendError(result.error ?? null);
      setSendState("error");
      return;
    }
    setSendState("idle");
    // Persist the submitted state (survives reload) and snapshot the answers,
    // so a later edit is distinguishable from "sent and untouched".
    onMarkSent(answers);
  }

  function handleImportClick() {
    fileInputRef.current?.click();
  }

  function handleImportFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = parseBriefingImport(
        String(reader.result),
        meta.company,
        config.id,
        config.foreignFileError
      );
      if (!result.ok) {
        setImportWarning(result.error);
        return;
      }
      if (result.companyMismatch) {
        const message = (actions.importMismatchWarning || "").replace(
          "{importedCompany}",
          result.importedCompany
        );
        if (!window.confirm(message)) return;
      }
      onImport(result.answers);
      setImportWarning(null);
    };
    reader.readAsText(file);
  }

  function handleReset() {
    if (window.confirm(actions.resetConfirm || "Zwischenstand wirklich löschen?")) {
      onReset();
    }
  }

  return (
    <div className="no-print bg-white border rounded-xl border-gray-200 shadow-sm">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full bg-abis-primary transition-all duration-300"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
          <span className="text-xs font-semibold text-gray-600 whitespace-nowrap tabular-nums">
            {percent} % · {answered}/{total}
          </span>
        </div>
        {hasHydrated ? (
          <p className="text-[11px] text-gray-400 mb-3">{actions.autosaveNotice}</p>
        ) : null}

        <button
          type="button"
          onClick={handlePrint}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-abis-primary text-white text-sm font-semibold px-3 py-2.5 hover:bg-abis-primary/90 transition-colors mb-3"
        >
          <Printer className="w-4 h-4" aria-hidden />
          {actions.savePdfLabel}
        </button>

        <div className="border-t border-gray-100 pt-3">
          {isSent && !changedSinceSent ? (
            /* Submitted and untouched — consent is locked, the action is done. */
            <div className="rounded-lg border border-abis-success/30 bg-abis-success/5 px-3 py-2.5">
              <p className="text-xs font-semibold text-abis-success flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" aria-hidden />
                {actions.sentTitle || "Briefing übermittelt"}
              </p>
              <p className="text-[11px] text-gray-500 mt-1">
                {sentLabel} · {actions.sendSuccessMessage}
              </p>
              <label className="flex items-start gap-2 text-[11px] text-gray-400 mt-2 cursor-not-allowed">
                <input type="checkbox" checked disabled className="sr-only" />
                <span className="mt-0.5 w-4 h-4 flex-shrink-0 rounded bg-gray-300 flex items-center justify-center">
                  <CheckCircle className="w-2.5 h-2.5 text-white" aria-hidden />
                </span>
                <span>{actions.sendConsentLabel}</span>
              </label>
              {actions.attachFilesLabel ? (
                <button
                  type="button"
                  onClick={openMailClient}
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-abis-primary hover:underline"
                >
                  <Mail className="w-3 h-3" aria-hidden />
                  {actions.attachFilesLabel}
                </button>
              ) : null}
            </div>
          ) : (
            <>
              {isSent && changedSinceSent ? (
                <p className="text-[11px] text-abis-warning font-medium mb-2 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" aria-hidden />
                  <span>
                    {(
                      actions.changedSinceSentHint ||
                      "Seit der Übermittlung ({sentAt}) geändert. Erneut senden, damit wir die aktuelle Fassung haben."
                    ).replace("{sentAt}", sentLabel)}
                  </span>
                </p>
              ) : null}

              {canSend ? (
                <label className="group flex items-start gap-2 text-[11px] text-gray-600 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={consentAccepted}
                    onChange={(event) => setConsentAccepted(event.target.checked)}
                    className="sr-only"
                  />
                  <span
                    className={`mt-0.5 w-4 h-4 flex-shrink-0 rounded flex items-center justify-center transition-all duration-150 ${
                      consentAccepted
                        ? "bg-abis-primary"
                        : "bg-gray-100 border-2 border-gray-300 group-hover:border-abis-primary/50"
                    }`}
                  >
                    {consentAccepted ? <CheckCircle className="w-2.5 h-2.5 text-white" /> : null}
                  </span>
                  <span>{actions.sendConsentLabel}</span>
                </label>
              ) : null}

              <button
                type="button"
                onClick={handleSendEmail}
                disabled={!canSend || !consentAccepted || sendState === "sending"}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-abis-accent text-white text-sm font-semibold px-3 py-2.5 hover:bg-abis-accent/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {sendState === "sending" ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                ) : (
                  <Send className="w-4 h-4" aria-hidden />
                )}
                {isSent ? actions.resendLabel || "Aktualisiertes Briefing senden" : actions.sendLabel}
              </button>

              {!canSend ? (
                <p className="text-[11px] text-abis-warning font-medium mt-1.5">
                  Noch {requiredMissing} Pflichtangabe{requiredMissing === 1 ? "" : "n"} offen
                  {actions.requiredMissingSuffix ? ` — ${actions.requiredMissingSuffix}` : "."}
                </p>
              ) : sendState === "error" ? (
                <div className="mt-1.5">
                  <p className="text-[11px] text-abis-error font-medium">{actions.sendErrorMessage}</p>
                  {sendError ? (
                    <p className="text-[10px] text-gray-400 mt-1 break-words font-mono leading-snug">
                      {sendError}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    onClick={copyAndOpenMail}
                    className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg border border-abis-error/30 text-abis-error text-[11px] px-2.5 py-1.5 hover:bg-abis-error/5 transition-colors"
                  >
                    <Copy className="w-3 h-3" aria-hidden />
                    {actions.sendFallbackButton}
                  </button>
                </div>
              ) : (
                <p className="text-[11px] text-gray-400 mt-1.5">{actions.sendHelp}</p>
              )}
            </>
          )}
        </div>

        <div className="border-t border-gray-100 mt-3 pt-3">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button type="button" onClick={handleCopy} className={utilityButton}>
              <Copy className="w-3.5 h-3.5" aria-hidden />
              {copyState === "copied" ? actions.copySuccessMessage : actions.copyLabel}
            </button>
            <button type="button" onClick={handleImportClick} className={utilityButton}>
              <Upload className="w-3.5 h-3.5" aria-hidden />
              {actions.importJsonLabel}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              onChange={handleImportFile}
              className="hidden"
            />
          </div>
          <button
            type="button"
            onClick={handleReset}
            className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-gray-400 hover:text-abis-error transition-colors py-1"
          >
            <RotateCcw className="w-3.5 h-3.5" aria-hidden />
            {actions.resetLabel}
          </button>
        </div>
        {importWarning ? <p className="text-xs text-abis-error mt-2">{importWarning}</p> : null}
      </div>
    </div>
  );
}
